import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CustomSelectOption from '../UserPersonalData/CustomSelectOption';
import PersonalInfoStandard from './PersonalInfoStandard';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useParams } from 'react-router-dom';
import { useAppData } from 'context/AppContext';
import { IUser } from 'types';
import { disableUser, enableUser } from 'repositories/firebase';
import { useAuth } from 'context/authContext';
import AccountStatusOption from '../UserPersonalData/AccountStatusOption';

const StandardUser = () => {
  const [user, setUser] = useState<IUser | null>(null);
  const { currentUser } = useAuth();

  const { fetchUserById } = useAppData();
  const { user_id } = useParams();

  useEffect(() => {
    const fetchUser = async () => {
      if (user_id) {
        const { data, error } = await fetchUserById(user_id);
        if (data) {
          setUser(data);
        } else {
          console.error('Error fetching user:', error);
          setUser(null);
        }
      }
    };
    fetchUser();
  }, [fetchUserById, user_id]);

  // const getStatusColor = (status: string) => {
  //   switch (status) {
  //     case 'approve':
  //       return 'green';
  //     case 'Deactivated':
  //       return 'red';
  //     case 'Pending':
  //       return 'yellow';
  //     default:
  //       return 'gray';
  //   }
  // };
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approve":
        return "green"; // Approved - Green color
      case "pending":
        return "yellow"; // Pending - Yellow color
      case "deactivated":
        return "red"; // Deactivated - Red color
      default:
        return "gray"; // Default color
    }
  };


  const handleAccountRoleChange = (event: any) => {
    const selectedRole = accountRoleOptions.find(
      (option) => option.value === event.target.value
    );
    setUser((prevUser) =>
      prevUser
        ? {
          ...prevUser,
          type: selectedRole ? selectedRole.value : event.target.value,
        }
        : null
    );
  };

  const handleAccountStatusChange = (event: any) => {
    const selectedStatus = accountStatusOptions.find(
      (option) => option.value === event.target.value
    );
    setUser((prevUser) =>
      prevUser
        ? {
          ...prevUser,
          accountStatus: selectedStatus
            ? selectedStatus.value
            : event.target.value,
        }
        : null
    );
  };

  const handleUserStatusChange = async (event: any) => {
    const selectedStatus = userStatusOptions.find(
      (option) => option.value === event.target.value
    );
    const newStatus = selectedStatus
      ? selectedStatus.value
      : event.target.value;

    setUser((prevUser) =>
      prevUser
        ? {
          ...prevUser,
          userStatus: newStatus,
        }
        : null
    );

    if (user && user.userStatus !== newStatus) {
      try {
        if (newStatus === 'active') {
          await enableUser(user.id);
        } else if (newStatus === 'disable') {
          await disableUser(user.id);
        }
      } catch (error) {
        console.error('Error updating user status:', error);
      }
    }
  };

  const handleAdminStatusChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUser((prevUser) =>
      prevUser ? { ...prevUser, isAdmin: event.target.checked } : null
    );
  };

  const accountRoleOptions = [
    { value: 'standard-user', label: 'Standard User' },
    { value: 'content-creator', label: 'Content Creator' },
    // { value: 'super-admin', label: 'Super Admin' },
  ];

  const accountStatusOptions = [
    { value: 'approve', label: 'Approved' },
    { value: 'pending', label: 'Pending' },
  ];

  const userStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'disable', label: 'Disable' },
  ];

  const handleNavigateBack = () => {
    window.history.back();
  };

  return (
    <>
      <Grid
        display='flex'
        flexDirection='column'
        justifyContent='center'
        gap={5}
        px={5}>
        <Box display='flex' justifyContent='space-between' gap={5}>
          <Box
            onClick={handleNavigateBack}
            fontWeight={500}
            fontSize='28px'
            color='#233F91'
            display='flex'
            alignItems='center'
            sx={{ cursor: 'pointer' }}
          >
            <KeyboardArrowLeftIcon
              sx={{ color: '#233F91', fontSize: '28px' }}
            />
            User Profile
          </Box>

          <Grid display='flex' pt={8} gap={2} alignItems='center'>
            <Box position='relative'>
              <Avatar
                alt={`${user?.firstName} ${user?.lastName}`}
                src={user?.profilePicture || ''}
                sx={{
                  width: '100px',
                  height: '100px',
                  bgcolor: user?.profilePicture ? 'transparent' : '#233F91',
                }}>
                {!user?.profilePicture && `${user?.firstName?.[0]}`}
              </Avatar>
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 2,
                  width: '20.12px',
                  height: '20.12px',
                }}>
                <img alt='edit' src='/assets/icons/affdownload.png' />
              </IconButton>
            </Box>
            <Box display='flex' flexDirection='column' gap={1} color='#595959'>
              <Box fontSize='23px' fontWeight={500}>
                {user?.firstName} {user?.lastName}
              </Box>
              <Box fontSize='16px' fontWeight={400} color='#000000'>
                {user?.email}
              </Box>
            </Box>
          </Grid>

          {/* field: type */}
          <Grid display='flex' justifyContent='space-between' pt={8}>
            <Grid item display='flex' flexDirection='column' gap={4}>
              <Box display='flex' gap={5} alignItems='center'>
                <Typography fontSize={18} fontWeight={700}>
                  Account Role
                </Typography>
                <Box
                  display='flex'
                  gap={1}
                  alignItems='center'
                  border='1px solid #D9D9D9'
                  borderRadius='30px'
                  p={1}>
                  <CustomSelectOption
                    value={
                      user
                        ? accountRoleOptions.find(
                          (option) => option.value === user?.type
                        )?.label || ''
                        : ''
                    }
                    onChange={handleAccountRoleChange}
                    options={accountRoleOptions}
                  />
                </Box>
              </Box>

              {/* field: accountStatus */}
              <Box display='flex' gap={5} alignItems='center'>
                <Typography fontSize={18} fontWeight={700}>
                  Account Status
                </Typography>
                <Box
                  display='flex'
                  alignItems='center'
                  gap={1}
                  border='1px solid #D9D9D9'
                  borderRadius='30px'
                  p={1}>
                  {/* <CustomSelectOption
                    value={
                      user
                        ? accountStatusOptions.find(
                          (option) => option.value === user?.accountStatus
                        )?.label || ''
                        : ''
                    }
                    onChange={handleAccountStatusChange}
                    options={accountStatusOptions}
                    icon={
                      <FiberManualRecordIcon
                        fontSize='small'
                        sx={{
                          color: getStatusColor(
                            user ? user?.accountStatus : ''
                          ),
                        }}
                      />
                    }
                  /> */}
                  <AccountStatusOption
                    value={user?.accountStatus || ""}
                    onChange={handleAccountStatusChange}
                    options={accountStatusOptions}
                    icon={
                      <FiberManualRecordIcon
                        fontSize="small"
                        sx={{ color: getStatusColor(user ? user.accountStatus : "") }}
                      />
                    }
                  />

                </Box>
              </Box>
            </Grid>

            {/* field: userStatus */}
            {currentUser?.userInfo?.type === 'super-admin' && (
              <Grid item display='flex' flexDirection='column' gap={4}>
                <Box display='flex' gap={5} alignItems='center'>
                  <Typography fontSize={18} fontWeight={700}>
                    User status
                  </Typography>
                  <Box
                    display='flex'
                    gap={1}
                    alignItems='center'
                    border='1px solid #D9D9D9'
                    borderRadius='30px'
                    p={1}>
                    <CustomSelectOption
                      value={
                        user
                          ? userStatusOptions.find(
                            (option) => option.value === user?.userStatus
                          )?.label || ''
                          : ''
                      }
                      onChange={handleUserStatusChange}
                      options={userStatusOptions}
                    />
                  </Box>
                </Box>

                {/* field: isAdmin */}
                <Box display='flex' gap={5} alignItems='center'>
                  <Typography fontSize={18} fontWeight={700}>
                    Admin
                  </Typography>
                  <Switch
                    checked={user?.isAdmin || false}
                    onChange={handleAdminStatusChange}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
        <PersonalInfoStandard user={user} />
      </Grid>
    </>
  );
};

export default StandardUser;
